import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { FlexColumn } from 'src/components/flex';
import ErrorPage, { SupportLink } from 'src/components/errors/ErrorPage';
import styles from 'src/components/errors/ErrorPage.module.css';
import shark500 from 'src/assets/img/errors/shark-500.webp';

type Props = {
    children: React.ReactNode;
    /** Root boundary: fill the viewport. Nested boundaries fill their container. */
    fullPage?: boolean;
    /** Boundary around a small region (e.g. the composer): compact alert instead of the full error page. */
    inline?: boolean;
};

export default function SentryErrorBoundary({ children, fullPage, inline }: Props) {
    const { pathname } = useLocation();

    const resetErrorRef = useRef<() => void>(() => {});

    useEffect(() => {
        if (resetErrorRef.current) {
            resetErrorRef.current();
        }
    }, [pathname]);


    return (
        <ErrorBoundary
            fallback={({ error, componentStack, resetError }) => {
                resetErrorRef.current = resetError;

                if (inline) {
                    return (
                        <Alert severity="error">
                            <Typography>Something went wrong. Please try refreshing the page.</Typography>
                            <Typography>You can let us know about this error by emailing <SupportLink />.</Typography>

                            <FlexColumn mt={2}>
                                <pre>{String(error)}</pre>
                                <pre>{componentStack}</pre>
                            </FlexColumn>
                        </Alert>
                    );
                }

                return (
                    <ErrorPage
                        fullPage={fullPage}
                        image={shark500}
                        title="Something went wrong"
                        message={<>An unexpected error occurred. Reloading usually fixes it. If it keeps happening, email <SupportLink />.</>}
                    >
                        <div className={styles.actions}>
                            <Button variant="contained" size="large" onClick={() => window.location.reload()} data-testid="error-boundary-reload-button">
                                Reload page
                            </Button>
                        </div>
                        <details className={styles.details}>
                            <summary><Typography component="span" variant="caption">Technical details</Typography></summary>
                            <pre>{String(error)}</pre>
                            <pre>{componentStack}</pre>
                        </details>
                    </ErrorPage>
                );
            }}
        >
            {children}
        </ErrorBoundary>
    );
}
